import { FieldArray, Form, Formik, getIn, useField, useFormikContext } from "formik";
import {
  ClipboardList,
  FileText,
  ListChecks,
  Plus,
  RotateCcw,
  Settings2,
  ShieldCheck,
  X,
} from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader } from "@/components/common/loader";
import { PageHeader } from "@/components/common/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RESOLUTION_TYPE_OPTIONS } from "@/features/cases/types";
import { CheckboxField } from "@/features/settings/components/checkbox-field";
import {
  FIELD_LABEL,
  SettingsSection,
  SettingsStickyBar,
} from "@/features/settings/components/settings-section";
import {
  type FirmSettings,
  type PoaMatter,
  type RequiredDocDefault,
  restorePoaDefaults,
  updateFirmSettings,
} from "@/features/settings/firm-api";
import { useFirmSettings } from "@/features/settings/hooks/use-firm-settings";
import { ApiError } from "@/lib/api";

// The case-steps editor keeps one newline-separated textarea per resolution
// type; convert to/from the stored `{ type: [step, ...] }` shape.
function stepsToText(steps: Record<string, string[]> = {}): Record<string, string> {
  const text: Record<string, string> = {};
  for (const { value } of RESOLUTION_TYPE_OPTIONS) {
    text[value] = (steps?.[value] ?? []).join("\n");
  }
  return text;
}

function textToSteps(text: Record<string, string>): Record<string, string[]> {
  const steps: Record<string, string[]> = {};
  for (const [type, block] of Object.entries(text)) {
    const lines = block
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
    if (lines.length) steps[type] = lines;
  }
  return steps;
}

const ROWS = 3;

// Pad/truncate the stored rows to a fixed 3-row grid for editing.
function toRows(matters: PoaMatter[]): PoaMatter[] {
  const rows = matters.slice(0, ROWS).map((m) => ({
    description: m.description ?? "",
    form: m.form ?? "",
    years: m.years ?? "",
  }));
  while (rows.length < ROWS) rows.push({ description: "", form: "", years: "" });
  return rows;
}

// Drop fully-empty rows before sending to the API.
function fromRows(rows: PoaMatter[]): PoaMatter[] {
  return rows.filter((r) => r.description.trim() || r.form.trim() || r.years.trim());
}

const matterSchema = Yup.array().of(
  Yup.object({
    description: Yup.string().max(40, "Max 40 characters"),
    form: Yup.string().max(30, "Max 30 characters"),
    years: Yup.string().max(30, "Max 30 characters"),
  }),
);

// Clone the stored requested-docs rows for editing; drop fully-empty rows on save.
function toDocRows(docs: RequiredDocDefault[]): RequiredDocDefault[] {
  return docs.map((d) => ({ name: d.name ?? "", notes: d.notes ?? "" }));
}

function fromDocRows(rows: RequiredDocDefault[]): RequiredDocDefault[] {
  return rows
    .map((r) => ({ name: r.name.trim(), notes: (r.notes ?? "").trim() }))
    .filter((r) => r.name);
}

const docsSchema = Yup.array().of(
  Yup.object({
    name: Yup.string().max(200, "Max 200 characters"),
    notes: Yup.string().max(300, "Max 300 characters"),
  }),
);

const schema = Yup.object({
  poaMattersIndividual: matterSchema,
  poaMattersBusiness: matterSchema,
  requiredDocsIndividual: docsSchema,
  requiredDocsBusiness: docsSchema,
});

interface DefaultsValues {
  poaRemoveRepName: boolean;
  poaUseRepPhone: boolean;
  poaCopyRep1: boolean;
  poaCopyRep2: boolean;
  poaAuthAccess: boolean;
  poaMattersIndividual: PoaMatter[];
  poaMattersBusiness: PoaMatter[];
  caseStepsText: Record<string, string>;
  requiredDocsIndividual: RequiredDocDefault[];
  requiredDocsBusiness: RequiredDocDefault[];
}

function CaseStepsField({ disabled }: { disabled: boolean }) {
  return (
    <div className="grid gap-3">
      <p className="text-muted-foreground text-xs">
        One step per line. A new case of each resolution type starts with these
        workflow steps.
      </p>
      {RESOLUTION_TYPE_OPTIONS.map((opt) => (
        <CaseStepsRow key={opt.value} type={opt.value} label={opt.label} disabled={disabled} />
      ))}
    </div>
  );
}

function CaseStepsRow({
  type,
  label,
  disabled,
}: {
  type: string;
  label: string;
  disabled: boolean;
}) {
  const [field] = useField(`caseStepsText.${type}`);
  return (
    <div className="grid gap-1">
      <Label className={FIELD_LABEL}>{label}</Label>
      <Textarea rows={3} disabled={disabled} placeholder="One step per line…" {...field} />
    </div>
  );
}

function MatterRow({ name, disabled }: { name: string; disabled: boolean }) {
  const [desc] = useField(`${name}.description`);
  const [form] = useField(`${name}.form`);
  const [years] = useField(`${name}.years`);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_auto_auto]">
      <Input placeholder="Description" maxLength={40} disabled={disabled} {...desc} />
      <Input placeholder="Form #" maxLength={30} disabled={disabled} className="sm:w-28" {...form} />
      <Input placeholder="Years / periods" maxLength={30} disabled={disabled} className="sm:w-40" {...years} />
    </div>
  );
}

/**
 * Per-section "Restore defaults" — resets that section's matter rows to the
 * system preset via the API, updating both the persisted settings and the live
 * form field. Confirmed first since it discards unsaved edits to the section.
 */
function RestoreDefaultsButton({
  section,
  name,
  label,
  onRestored,
}: {
  section: "ind" | "bus";
  name: string;
  label: string;
  onRestored: (settings: FirmSettings) => void;
}) {
  const { setFieldValue } = useFormikContext<DefaultsValues>();
  const [busy, setBusy] = useState(false);

  async function handleRestore() {
    setBusy(true);
    try {
      const updated = await restorePoaDefaults(section);
      onRestored(updated);
      const rows = section === "bus" ? updated.poaMattersBusiness : updated.poaMattersIndividual;
      await setFieldValue(name, toRows(rows));
      toast.success("Defaults restored");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't restore defaults.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="w-fit">
          <RotateCcw /> Restore defaults
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Restore {label} defaults?</AlertDialogTitle>
          <AlertDialogDescription>
            This replaces the {label.toLowerCase()} tax-matter rows with the system
            preset. Unsaved edits to this section will be lost.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={handleRestore}>
            Restore
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function MatterTable({
  name,
  title,
  section,
  disabled,
  onRestored,
}: {
  name: string;
  title: string;
  section: "ind" | "bus";
  disabled: boolean;
  onRestored: (settings: FirmSettings) => void;
}) {
  return (
    <div className="grid gap-2">
      <Label className={FIELD_LABEL}>{title}</Label>
      {Array.from({ length: ROWS }, (_, i) => (
        <MatterRow key={i} name={`${name}[${i}]`} disabled={disabled} />
      ))}
      <RestoreDefaultsButton
        section={section}
        name={name}
        label={section === "bus" ? "Business" : "Individual"}
        onRestored={onRestored}
      />
    </div>
  );
}

function DocRow({
  name,
  disabled,
  onRemove,
}: {
  name: string;
  disabled: boolean;
  onRemove: () => void;
}) {
  const [nameField] = useField(`${name}.name`);
  const [notesField] = useField(`${name}.notes`);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
      <Input placeholder="Document name" maxLength={200} disabled={disabled} {...nameField} />
      <Input placeholder="Notes (optional)" maxLength={300} disabled={disabled} {...notesField} />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        disabled={disabled}
        onClick={onRemove}
        aria-label="Remove document"
      >
        <X />
      </Button>
    </div>
  );
}

/** Variable-length editable list of requested documents for one client type. */
function RequestedDocsTable({
  name,
  title,
  disabled,
}: {
  name: string;
  title: string;
  disabled: boolean;
}) {
  return (
    <div className="grid gap-2">
      <Label className={FIELD_LABEL}>{title}</Label>
      <FieldArray name={name}>
        {({ push, remove, form }) => {
          const rows: RequiredDocDefault[] = getIn(form.values, name) ?? [];
          return (
            <div className="grid gap-2">
              {rows.length === 0 && (
                <p className="text-muted-foreground text-xs">No documents yet.</p>
              )}
              {rows.map((_, i) => (
                <DocRow
                  key={i}
                  name={`${name}[${i}]`}
                  disabled={disabled}
                  onRemove={() => remove(i)}
                />
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-fit"
                disabled={disabled}
                onClick={() => push({ name: "", notes: "" })}
              >
                <Plus /> Add document
              </Button>
            </div>
          );
        }}
      </FieldArray>
    </div>
  );
}

export function DefaultsTab() {
  const { settings, setSettings, loading, error, isAdmin } = useFirmSettings();

  async function handleSubmit(values: DefaultsValues) {
    try {
      const updated = await updateFirmSettings({
        poaRemoveRepName: values.poaRemoveRepName,
        poaUseRepPhone: values.poaUseRepPhone,
        poaCopyRep1: values.poaCopyRep1,
        poaCopyRep2: values.poaCopyRep2,
        poaAuthAccess: values.poaAuthAccess,
        poaMattersIndividual: fromRows(values.poaMattersIndividual),
        poaMattersBusiness: fromRows(values.poaMattersBusiness),
        defaultCaseSteps: textToSteps(values.caseStepsText),
        defaultRequiredDocuments: {
          individual: fromDocRows(values.requiredDocsIndividual),
          business: fromDocRows(values.requiredDocsBusiness),
        },
      });
      setSettings(updated);
      toast.success("Defaults saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save changes.");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Defaults"
        subtitle="Presets applied to new POA / TIA authorizations and new cases."
      />

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive text-sm">
          Couldn&apos;t load this section. Please refresh and try again.
        </p>
      ) : (
        settings && (
          <Formik<DefaultsValues>
            initialValues={{
              poaRemoveRepName: settings.poaRemoveRepName,
              poaUseRepPhone: settings.poaUseRepPhone,
              poaCopyRep1: settings.poaCopyRep1,
              poaCopyRep2: settings.poaCopyRep2,
              poaAuthAccess: settings.poaAuthAccess,
              poaMattersIndividual: toRows(settings.poaMattersIndividual),
              poaMattersBusiness: toRows(settings.poaMattersBusiness),
              caseStepsText: stepsToText(settings.defaultCaseSteps),
              requiredDocsIndividual: toDocRows(settings.defaultRequiredDocuments.individual),
              requiredDocsBusiness: toDocRows(settings.defaultRequiredDocuments.business),
            }}
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, dirty }) => (
              <Form className="flex flex-col gap-6">
                <fieldset disabled={!isAdmin} className="flex flex-col gap-6">
                  <SettingsSection
                    title="POA / TIA options"
                    icon={ShieldCheck}
                    bodyClassName="block"
                  >
                    <div className="grid gap-3">
                      <CheckboxField name="poaRemoveRepName" label="Remove representative name from printed forms" />
                      <CheckboxField name="poaUseRepPhone" label="Use the representative's phone number" />
                      <CheckboxField name="poaCopyRep1" label="Send copies of notices to representative 1" />
                      <CheckboxField name="poaCopyRep2" label="Send copies of notices to representative 2" />
                      <CheckboxField name="poaAuthAccess" label="Authorize online account access" />
                    </div>
                  </SettingsSection>

                  <SettingsSection title="Default tax matters" icon={ClipboardList} bodyClassName="block">
                    <div className="grid gap-6">
                      <MatterTable
                        name="poaMattersIndividual"
                        title="Default tax matters — Individuals"
                        section="ind"
                        disabled={!isAdmin}
                        onRestored={setSettings}
                      />
                      <MatterTable
                        name="poaMattersBusiness"
                        title="Default tax matters — Businesses"
                        section="bus"
                        disabled={!isAdmin}
                        onRestored={setSettings}
                      />
                    </div>
                  </SettingsSection>

                  <SettingsSection title="Default case steps" icon={ListChecks} bodyClassName="block">
                    <CaseStepsField disabled={!isAdmin} />
                  </SettingsSection>

                  <SettingsSection title="Requested documents" icon={FileText} bodyClassName="block">
                    <div className="grid gap-6">
                      <p className="text-muted-foreground text-xs">
                        Documents to request from each new client, by type. The
                        onboarding &ldquo;Request docs&rdquo; step pre-fills from these.
                      </p>
                      <RequestedDocsTable
                        name="requiredDocsIndividual"
                        title="Individuals"
                        disabled={!isAdmin}
                      />
                      <RequestedDocsTable
                        name="requiredDocsBusiness"
                        title="Businesses"
                        disabled={!isAdmin}
                      />
                    </div>
                  </SettingsSection>
                </fieldset>

                <SettingsSection title="More defaults" icon={Settings2} bodyClassName="block">
                  <div className="flex flex-wrap gap-3 text-sm">
                    <Link className="text-primary hover:underline" to="/settings/tags">
                      Tags Library
                    </Link>
                    <Link className="text-primary hover:underline" to="/settings/notifications">
                      Notifications
                    </Link>
                    <Link className="text-primary hover:underline" to="/settings/emails">
                      Email Templates
                    </Link>
                    <Link className="text-primary hover:underline" to="/settings/folders">
                      Folder Structure
                    </Link>
                    <Link className="text-primary hover:underline" to="/settings/signatures">
                      Signatures
                    </Link>
                  </div>
                </SettingsSection>

                {!isAdmin ? (
                  <p className="text-muted-foreground text-sm">
                    Only a country director can edit defaults.
                  </p>
                ) : (
                  <SettingsStickyBar>
                    <Button type="submit" disabled={isSubmitting || !dirty}>
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </Button>
                  </SettingsStickyBar>
                )}
              </Form>
            )}
          </Formik>
        )
      )}
    </div>
  );
}
